import { useQuery } from "@apollo/client";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { configArrayNew } from "components/Environment/configArray";
import EnvironmentContextProvider from "components/Environment/EnvironmentContext";
import EnvironmentPage from "components/Environment/view/EnvironmentPage";
import NotFoundPage from "components/error/NotFoundPage";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";

import StackHeader from "../components/Header";
import { StackContext } from "../Context";
import { getStacksBackUrl } from "../helpers";
import { GET_ENVIRONMENT } from "./gql";

const EnvironmentNew = () => {
  const { onError } = useTypedContext(FlashContext);
  const { stack: stackCtx, hasAtLeastWriteAccess } = useTypedContext(StackContext);

  useTitle(`Environment · ${stackCtx.name}`);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stackCtx.name,
    },
  ]);

  const { error, loading, data } = useQuery<{ stack: Stack }>(GET_ENVIRONMENT, {
    onError,
    variables: { stackId: stackCtx.id },
  });

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const stack = { ...stackCtx, ...data?.stack };

  const [localVariables, spaceliftVariables, mountedFiles] = configArrayNew(stack.runtimeConfig);

  return (
    <>
      <StackHeader />
      <EnvironmentContextProvider
        resourceId={stack.id}
        refetchQueries={[GET_ENVIRONMENT]}
        canCreateAndEditVariables={hasAtLeastWriteAccess}
      >
        <EnvironmentPage
          localVariables={localVariables}
          mountedFiles={mountedFiles}
          spaceliftVariables={spaceliftVariables}
          attachedContexts={stack.attachedContexts}
        />
      </EnvironmentContextProvider>
    </>
  );
};

export default EnvironmentNew;
